import React, { Component } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import {
  buildConfiguredPriceDriverCombinationRows,
  pivotQualityAttributes,
  toQualityAttributeValueHash,
} from "../../common/services/products/productUtilities";
import { getSpotByTermSchedule } from "../../common/services/markets/termService";
import {
  aggregateMarks,
  aggregateMarksWithoutAttributes,
} from "../../common/services/marketdata/marketDataUtilities";
import {
  getFiniteValuesForKeys,
  getScaledBoundsFromValues,
  joinArrays,
  toFiniteNumber,
} from "../../common/services/utilities";
import MarkCell from "./markCell";
import CandlestickChart from "../../common/components/d3/candlestickChart";
import { withTranslation } from "react-i18next";
import uuid from "react-uuid";
import { HistoricalReportingPeriodicity } from "../../common/enumerations/enums";

class HistoricalMarketDataTable extends Component {
  state = {
    marks: [],
    summaryRow: null,
    qualityAttributeValueHash: null,
    candlestickMarksRaw: [],
    startDate: null,
    endDate: null,
    yMax: null,
    yMin: null,
    columns: [],
    pageSize: 20,
    currentPage: 1,
    isLoading: true,
  };

  async componentDidMount() {
    await this.initializeTable(this.props);
  }

  async componentDidUpdate(previousProps) {
    if (
      previousProps.fromDate !== this.props.fromDate ||
      previousProps.toDate !== this.props.toDate ||
      !_.isEqual(previousProps.marks, this.props.marks)
    ) {
      await this.initializeTable(this.props);
    }
  }

  async initializeTable(passedProps) {
    const {
      marks,
      product,
      fromDate,
      toDate,
      termScheduleId,
      marketPriceDriverCombinations,
      historicalReportingPeriodicity,
    } = passedProps;

    const isYearlyHistory =
      historicalReportingPeriodicity === HistoricalReportingPeriodicity.Year;
    const terms = isYearlyHistory
      ? this.buildDailyTermsFromMarks(marks)
      : await getSpotByTermSchedule(
          termScheduleId,
          fromDate.toLocaleDateString("fr-CA"),
          toDate.toLocaleDateString("fr-CA")
        );

    const aggregatedMarks = isYearlyHistory ? marks : aggregateMarks(marks);

    const configuredCombinationRows = buildConfiguredPriceDriverCombinationRows(
      product,
      marketPriceDriverCombinations
    );
    const combinationRows =
      configuredCombinationRows.length > 0
        ? configuredCombinationRows
        : this.buildProductCombinationRows(product);

    const columns = this.mapToColumns(combinationRows);

    const { rows, summaryRow } = this.joinMarks(
      aggregatedMarks,
      terms,
      combinationRows
    );

    const validHashes = new Set(
      combinationRows.map((row) => row.qualityAttributeValueHash)
    );

    const termKeys = new Set(terms.map((t) => t.marketDate + t.termId));

    const candlestickMarksRaw = marks.filter(
      (m) =>
        validHashes.has(m.qualityAttributeValueHash) &&
        termKeys.has(m.marketDate + m.termId)
    );
    const candlestickSummary = this.buildCandleStickSummary(
      candlestickMarksRaw
    );

    this.setState({
      marks: rows,
      summaryRow,
      candlestickMarksRaw: candlestickMarksRaw,
      candlestickSummary,
      startDate: new Date(fromDate),
      endDate: new Date(toDate),
      columns: columns,
      isLoading: false,
    });
  }

  buildDailyTermsFromMarks(marks = []) {
    const termsByMarketDate = marks.reduce((result, mark) => {
      if (!mark.marketDate || result[mark.marketDate]) {
        return result;
      }

      result[mark.marketDate] = {
        marketDate: mark.marketDate,
        termId: mark.termId,
        name: mark.termName,
      };

      return result;
    }, {});

    return Object.values(termsByMarketDate).sort((a, b) =>
      new Date(a.marketDate) > new Date(b.marketDate) ? 1 : -1
    );
  }

  buildProductCombinationRows(product) {
    return pivotQualityAttributes(product).map((row) => ({
      ...row,
      qualityAttributeValueHash: toQualityAttributeValueHash(
        row.qualityAttributeValues
      ),
    }));
  }

  mapToColumns(combinationRows) {
    const {
      organizationId,
      marketId,
      marketDate,
      historicalReportingPeriodicity,
      priceBasisId,
      currencyId,
      uomId,
      pricePrecision,
      volumePrecision,
      currencySymbol,
    } = this.props;

    var columnDefs = [
      {
        field: "marketDate",
        headerName: this.props.t("MarketDate"),
        cellRenderer: (params) => {
          const data = params.data;
          if (data.marketDate === "Average") {
            return <span>{data.marketDate}</span>;
          }
          return (
            <span>
              {data.marketDate.slice(0, 10) +
                (historicalReportingPeriodicity === "Hour"
                  ? "H" + data.marketDate.slice(11, 13)
                  : "")}
            </span>
          );
        },
        flex: 1,
        filter: false,
        pinned: "left",
        suppressMovable: true,
      },
      ...combinationRows.map((x) => ({
        field: x.qualityAttributeValueHash,
        headerName: Object.keys(x.qualityAttributeValues)
          .map(function (k) {
            return x.qualityAttributeValues[k].value;
          })
          .join("|"),
        cellRenderer: (params) => {
          const data = params.data;
          return (
            <MarkCell
              termId={data.termId}
              organizationId={organizationId}
              marketId={marketId}
              priceBasisId={priceBasisId}
              currencyId={currencyId}
              uomId={uomId}
              pricePrecision={pricePrecision}
              volumePrecision={volumePrecision}
              currencySymbol={currencySymbol}
              marketDate={marketDate}
              mark={
                data[x.qualityAttributeValueHash] ?? {
                  markId: uuid(),
                  eventId: uuid(),
                  active: true,
                }
              }
              qualityAttributeValues={data.qualityAttributeValues}
              shortName={data["shortName"]}
              //isNew={data[term.termId] ? false: true}
            />
          );
        },
        filter: false,
        flex: 1,
      })),
    ];

    return columnDefs;
  }

  joinMarks(aggregatedMarks, terms, combinationRows) {
    const validHashes = new Set(
      (combinationRows || []).map((row) => row.qualityAttributeValueHash)
    );
    const termIds = new Set(terms.map((x) => x.termId));
    const filteredMarks = aggregatedMarks.filter(
      (x) =>
        termIds.has(x.termId) &&
        validHashes.has(x.qualityAttributeValueHash)
    );

    //Pivot the marks
    const groupedByMarketDate = filteredMarks.reduce((result, obj) => {
      const { qualityAttributeValueHash, marketDate, ...rest } = obj;

      if (!result[marketDate]) {
        result[marketDate] = {};
        result[marketDate]["marketDate"] = marketDate;
      }

      //Add a spot key so we don't mix marks from same instrument/different days
      const spotKey = qualityAttributeValueHash;
      result[marketDate][spotKey] = { ...rest };

      return result;
    }, {});

    const pivotedMarks = Object.entries(groupedByMarketDate).map(
      ([qualityAttributeValueHash, attributes]) => ({
        qualityAttributeValueHash,
        ...attributes,
      })
    );

    const joinedData = joinArrays(terms, pivotedMarks, "marketDate");

    // Initialize an object to store sums and counts for each hash so we can calculate Averages
    let hashStats = {};

    // Iterate over each object in the array
    joinedData.forEach((obj) => {
      // Iterate over each hash in the object
      Object.keys(obj).forEach((key) => {
        // Check if the key is a hash (32 characters long)
        if (key.length === 64) {
          // Initialize hash statistics if not already present
          if (!hashStats[key]) {
            hashStats[key] = { sum: 0, count: 0 };
          }
          // Add the price to the sum and increment the count
          const numericPrice = toFiniteNumber(obj[key].price);
          if (numericPrice != null) {
            hashStats[key].sum += numericPrice;
            hashStats[key].count++;
          }
        }
      });
    });

    // Calculate the average for each hash
    let hashAverages = { marketDate: "Average" };
    Object.keys(hashStats).forEach((key) => {
      if (hashStats[key].count > 0) {
        hashAverages[key] = {
          price: hashStats[key].sum / hashStats[key].count,
        };
      }
    });

    return {
      rows: joinedData,
      summaryRow: hashAverages,
    };
  }

  buildCandleStickSummary(candlestickMarksRaw = []) {
    // Aggregate the raw marks to get high/low/open/close values
    const aggregatedData = aggregateMarksWithoutAttributes(candlestickMarksRaw);

    // Collect all raw data values (price, bid, offer)
    const rawDataValues = getFiniteValuesForKeys(candlestickMarksRaw, [
      "price",
      "bid",
      "offer",
    ]);

    // Collect all aggregated data values (high, low, open, close)
    const aggregatedDataValues = getFiniteValuesForKeys(aggregatedData, [
      "high",
      "low",
      "open",
      "close",
    ]);

    // Calculate min/max from both raw and aggregated data
    const allValues = [...rawDataValues, ...aggregatedDataValues];
    const { yMin, yMax } = getScaledBoundsFromValues(allValues);

    return {
      yMax: yMax, // scale to within 20% for aesthetic reasons
      yMin: yMin, // scale to within 20% for aesthetic reasons
    };
  }

  getCandleStickMarks() {
    const {
      candlestickMarksRaw,
      candlestickSummary,
      qualityAttributeValueHash,
    } = this.state;

    const candlestickMarks = candlestickMarksRaw.filter(
      (x) =>
        x.qualityAttributeValueHash ===
        (qualityAttributeValueHash ?? x.qualityAttributeValueHash)
    );

    return {
      candlestickMarks: candlestickMarks,
      yMax: candlestickSummary?.yMax,
      yMin: candlestickSummary?.yMin,
    };
  }

  render() {
    const { isLoading, columns, marks, summaryRow, startDate, endDate } =
      this.state;

    const { yMin, yMax, candlestickMarks } = this.getCandleStickMarks();

    if (isLoading) return <Loading />;

    const { currencySymbol, pricePrecision, historicalReportingPeriodicity } =
      this.props;

    return (
      <div className="container-fluid">
        <div className="row market-data-graph">
          <CandlestickChart
            data={candlestickMarks ?? []}
            qualityAttributes={this.props.product?.qualityAttributes}
            historicalReportingPeriodicity={historicalReportingPeriodicity}
            startDate={startDate}
            endDate={endDate}
            yMin={yMin}
            yMax={yMax}
            currencySymbol={currencySymbol}
            pricePrecision={pricePrecision}
          />
        </div>
        <div className="row marks-table">
          <ThemeableAgGrid
            className="table-condensed"
            columnDefs={columns}
            rowData={marks}
            pinnedBottomRowData={summaryRow ? [summaryRow] : []}
            getRowId={(params) => params.data.marketDate}
            allowHorizontalScroll={false}
          />
        </div>
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(HistoricalMarketDataTable);
